import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/shared/lib/supabase';
import { Icons } from '@/shared/constants/icons';
import { Skeleton } from '@/shared/components/Skeleton';
import { useToast } from '@/shared/components/Toast';
import { formatDate } from '@/shared/lib/intl';
import { getApiErrorMessage } from '@/shared/lib/api-errors';
import { confirmDialog } from '@/shared/stores/confirm.store';
import { securityService, type RecoveryCodeStatus } from '@/shared/services/security.service';

type Factor = {
  id: string;
  factor_type: 'totp' | 'phone';
  status: 'verified' | 'unverified';
  friendly_name?: string;
  created_at: string;
};

export interface TwoFactorSetupProps {
  /** Called once a new factor is verified (the session is now aal2). */
  onEnabled?: () => void;
  /** Offer "Turn off". Staff accounts must keep it on, so their screen hides it. */
  allowDisable?: boolean;
}

const codeInputClass = 'input font-mono tracking-[0.2em]';

/** Six digits only, for the authenticator code fields. */
function digits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

/**
 * The recovery codes, shown once (AL-15): copy, download, then "I have saved
 * them". Nothing here is stored in the browser.
 */
function RecoveryCodesPanel({ codes, email, onDone }: { codes: string[]; email: string | null; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const { toast } = useToast();
  const text = [t('settings.securityTab.recoveryFileHeading', { email: email ?? '' }), '', ...codes].join('\n');

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast('success', t('settings.securityTab.recoveryCopied'));
    } catch {
      toast('error', t('settings.securityTab.recoveryCopyFailed'));
    }
  };

  const download = (): void => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'unipostudio-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-4 p-4 rounded-[10px] border border-[var(--line)] bg-[var(--color-surface-2)]" role="region" aria-labelledby="recovery-codes-title">
      <h3 id="recovery-codes-title" className="text-body font-semibold text-[var(--ink)] m-0 mb-1">{t('settings.securityTab.recoveryTitle')}</h3>
      <p className="text-ui text-[var(--ink-2)] leading-[1.55] m-0 mb-3">{t('settings.securityTab.recoveryShowOnce')}</p>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-ui text-[var(--ink)] list-none p-0 m-0 mb-4" dir="ltr">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void copy()}>
          {t('settings.securityTab.recoveryCopy')}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={download}>
          {t('settings.securityTab.recoveryDownload')}
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={onDone}>
          {t('settings.securityTab.recoverySaved')}
        </button>
      </div>
    </div>
  );
}

/**
 * Two-step verification (TOTP) on the Supabase Auth SDK: Settings, Security
 * for everyone, and the staff console's "turn it on first" screen (staff
 * cannot open Settings, and the API refuses their console calls without it).
 *
 * Supabase itself does not ask for the code: a sign-in lands at aal1, and the
 * app (useAuthMfa, MfaChallenge) holds the person at the code step until the
 * session is aal2; the API refuses aal1 tokens for people with a factor.
 *
 *  1. "Turn on" calls enroll(), which returns the factor id, a TOTP secret and
 *     a QR-code data URL.
 *  2. The person scans the QR with an authenticator app and types the code,
 *     which is challenged and verified.
 *  3. The factor turns verified, the session is raised to aal2, and ten
 *     recovery codes are made and shown once (AL-15).
 *  4. "Turn off" asks for a fresh code from the app first (an unlocked laptop
 *     is not enough), then unenrolls and removes the recovery codes.
 */
export function TwoFactorSetup({ onEnabled, allowDisable = true }: TwoFactorSetupProps): JSX.Element {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [factors, setFactors] = useState<Factor[] | null>(null);
  const [loadingFactors, setLoadingFactors] = useState(true);
  const [email, setEmail] = useState<string | null>(null);

  // Enrollment-in-progress state — set when the user clicks "Turn on"
  // and reset when they finish (verify) or cancel.
  const [pending, setPending] = useState<{ factorId: string; qrCode: string; secret: string } | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [busy, setBusy] = useState(false);
  // Turning off: the fresh code the person types first.
  const [disabling, setDisabling] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [codeStatus, setCodeStatus] = useState<RecoveryCodeStatus | null>(null);

  const loadCodeStatus = async (): Promise<void> => {
    try {
      setCodeStatus(await securityService.recoveryCodeStatus());
    } catch {
      setCodeStatus(null);
    }
  };

  const refreshFactors = async (): Promise<void> => {
    setLoadingFactors(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      const all: Factor[] = [...(data.totp ?? []), ...(data.phone ?? [])] as Factor[];
      setFactors(all);
      if (all.some((f) => f.factor_type === 'totp' && f.status === 'verified')) void loadCodeStatus();
    } catch (err) {
      toast('error', getApiErrorMessage(err, t('settings.securityTab.loadFailed')));
      setFactors([]);
    } finally {
      setLoadingFactors(false);
    }
  };

  useEffect(() => {
    void refreshFactors();
    void supabase.auth.getSession().then(({ data }) => setEmail(data.session?.user.email ?? null)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifiedTotp = (factors ?? []).find((f) => f.factor_type === 'totp' && f.status === 'verified');

  const makeRecoveryCodes = async (): Promise<void> => {
    try {
      setNewCodes(await securityService.createRecoveryCodes());
      void loadCodeStatus();
    } catch (err) {
      toast('error', getApiErrorMessage(err, t('settings.securityTab.recoveryFailed')));
    }
  };

  const remakeRecoveryCodes = async (): Promise<void> => {
    if (!(await confirmDialog({ title: t('settings.securityTab.recoveryRemakeConfirm'), confirmLabel: t('settings.securityTab.recoveryRemake') }))) return;
    setBusy(true);
    try {
      await makeRecoveryCodes();
    } finally {
      setBusy(false);
    }
  };

  const startEnroll = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      // Clean up any prior unverified factor before enrolling a fresh
      // one — Supabase will refuse if you already have an unverified
      // factor sitting around.
      const stale = (factors ?? []).find((f) => f.factor_type === 'totp' && f.status === 'unverified');
      if (stale) {
        await supabase.auth.mfa.unenroll({ factorId: stale.id }).catch(() => {});
      }
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'UniPostudio' });
      if (error) throw error;
      setPending({ factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret });
      setVerifyCode('');
    } catch (err) {
      toast('error', getApiErrorMessage(err, t('settings.securityTab.enrollFailed')));
    } finally {
      setBusy(false);
    }
  };

  const cancelEnroll = async (): Promise<void> => {
    if (!pending) return;
    setBusy(true);
    try {
      await supabase.auth.mfa.unenroll({ factorId: pending.factorId });
    } catch {
      // best-effort
    } finally {
      setPending(null);
      setVerifyCode('');
      setBusy(false);
    }
  };

  const confirmEnroll = async (): Promise<void> => {
    if (!pending || verifyCode.length !== 6 || busy) return;
    setBusy(true);
    try {
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId: pending.factorId });
      if (chErr) throw chErr;
      const { error: verifyErr } = await supabase.auth.mfa.verify({ factorId: pending.factorId, challengeId: challenge.id, code: verifyCode });
      if (verifyErr) throw verifyErr;
      toast('success', t('settings.securityTab.enabledToast'));
      setPending(null);
      setVerifyCode('');
      await refreshFactors();
      // The session is aal2 now: the API makes the codes.
      await makeRecoveryCodes();
      onEnabled?.();
    } catch (err) {
      toast('error', getApiErrorMessage(err, t('settings.securityTab.verifyFailed')));
    } finally {
      setBusy(false);
    }
  };

  /** Turning off needs a fresh code from the app (AL-15), then the factor and the recovery codes go. */
  const disableFactor = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!verifiedTotp || disableCode.length !== 6 || busy) return;
    setBusy(true);
    try {
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId: verifiedTotp.id });
      if (chErr) throw chErr;
      const { error: verifyErr } = await supabase.auth.mfa.verify({ factorId: verifiedTotp.id, challengeId: challenge.id, code: disableCode });
      if (verifyErr) throw verifyErr;
      const { error } = await supabase.auth.mfa.unenroll({ factorId: verifiedTotp.id });
      if (error) throw error;
      await securityService.deleteRecoveryCodes().catch(() => undefined);
      toast('success', t('settings.securityTab.disabledToast'));
      setDisabling(false);
      setDisableCode('');
      setCodeStatus(null);
      await refreshFactors();
    } catch (err) {
      setDisableCode('');
      toast('error', getApiErrorMessage(err, t('settings.securityTab.disableFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card-compact" aria-labelledby="two-factor-title">
      <h2 id="two-factor-title" className="stat-label mb-1.5">{t('settings.securityTab.twoFactorTitle')}</h2>
      <p className="text-ui text-[var(--ink-2)] leading-[1.55] mt-0 mb-4 max-w-prose">{t('settings.securityTab.twoFactorHint')}</p>

      {loadingFactors ? (
        <Skeleton height={60} width="100%" rounded="md" />
      ) : verifiedTotp ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-[10px] bg-[var(--good-soft)]">
            <div className="flex items-center gap-2 text-ui text-[var(--good)]">
              <Icons.check size={16} aria-hidden="true" />
              <span>
                <strong>{t('settings.securityTab.enabled')}</strong> ·{' '}
                {t('settings.securityTab.addedOn', { date: formatDate(verifiedTotp.created_at, { year: 'numeric', month: 'short', day: 'numeric' }) })}
              </span>
            </div>
            {allowDisable && !disabling && (
              <button type="button" onClick={() => setDisabling(true)} disabled={busy} className="btn btn-secondary btn-sm text-[var(--bad)]">
                {t('settings.securityTab.disable')}
              </button>
            )}
          </div>

          {allowDisable && disabling && (
            <form onSubmit={disableFactor} className="mt-3 p-3 rounded-[10px] bg-[var(--color-surface-2)] max-w-md">
              <label htmlFor="totp-disable-code" className="text-meta font-medium text-[var(--ink-2)] block mb-1.5">
                {t('settings.securityTab.disableCodeHint')}
              </label>
              <input
                id="totp-disable-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                dir="ltr"
                value={disableCode}
                onChange={(e) => setDisableCode(digits(e.target.value))}
                disabled={busy}
                className={codeInputClass}
              />
              <div className="flex flex-wrap gap-2 mt-3">
                <button type="submit" disabled={busy || disableCode.length !== 6} className="btn btn-destructive btn-sm">
                  {t('settings.securityTab.disableConfirmButton')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDisabling(false);
                    setDisableCode('');
                  }}
                  disabled={busy}
                  className="btn btn-ghost btn-sm"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}

          {newCodes ? (
            <RecoveryCodesPanel codes={newCodes} email={email} onDone={() => setNewCodes(null)} />
          ) : (
            <div className="mt-4">
              <h3 className="text-ui font-semibold text-[var(--ink)] m-0 mb-1">{t('settings.securityTab.recoveryTitle')}</h3>
              <p className="text-ui text-[var(--ink-2)] leading-[1.55] m-0 mb-2 max-w-prose">
                {codeStatus && codeStatus.remaining > 0
                  ? `${t('settings.securityTab.recoveryHint')} ${t('settings.securityTab.recoveryLeft', { left: codeStatus.remaining })}`
                  : t('settings.securityTab.recoveryNone')}
              </p>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy}
                onClick={() => void (codeStatus && codeStatus.remaining > 0 ? remakeRecoveryCodes() : makeRecoveryCodes())}
              >
                {codeStatus && codeStatus.remaining > 0 ? t('settings.securityTab.recoveryRemake') : t('settings.securityTab.recoveryMake')}
              </button>
            </div>
          )}
        </>
      ) : pending ? (
        <div className="space-y-3">
          <p className="text-ui text-[var(--ink-2)] m-0">{t('settings.securityTab.scanQr')}</p>
          <div className="flex items-start gap-4 flex-wrap">
            <div className="rounded-[10px] border border-[var(--line)] p-3 bg-[var(--color-surface)] min-w-[180px]">
              {/* Supabase returns a data URL — render directly. */}
              <img src={pending.qrCode} alt={t('settings.securityTab.qrAlt')} width={160} height={160} />
            </div>
            <div className="flex-1 min-w-0 sm:min-w-[220px]">
              <div className="text-meta text-[var(--ink-3)] mb-1">{t('settings.securityTab.secretManual')}</div>
              <div className="font-mono text-ui text-[var(--ink)] bg-[var(--color-surface-2)] border border-[var(--line)] rounded-[10px] py-2 px-3 mb-3 break-all" dir="ltr">
                {pending.secret}
              </div>
              <label className="text-meta font-medium text-[var(--ink-2)] block mb-1.5" htmlFor="totp-code">
                {t('settings.securityTab.codeLabel')}
              </label>
              <input
                id="totp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                dir="ltr"
                value={verifyCode}
                onChange={(e) => setVerifyCode(digits(e.target.value))}
                disabled={busy}
                className={codeInputClass}
              />
              <div className="flex items-center gap-2 mt-3">
                <button type="button" onClick={confirmEnroll} disabled={busy || verifyCode.length !== 6} className="btn btn-primary btn-sm">
                  {busy ? t('settings.securityTab.verifying') : t('settings.securityTab.enable')}
                </button>
                <button type="button" onClick={cancelEnroll} disabled={busy} className="btn btn-ghost btn-sm">
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <button type="button" onClick={startEnroll} disabled={busy} className="btn btn-secondary btn-sm">
          {busy ? t('settings.securityTab.settingUp') : t('settings.securityTab.enable')}
        </button>
      )}
    </section>
  );
}
